import { BybDropdown } from "@Components/common/byb-ds";
import { DSIcon } from "@Components/common/byb-ds/icons";
import { BybButtonVR, BybDialog } from "@Components/game/common";
import { HeaderBase } from "@Components/game/header/styled-components";
import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import Icon from "@Components/generic/icon";
import { Actions, Events } from "@Context/common/general/context";
import AnalyticsEvents from "@Enums/AnalyticsEvents";
import { useApi } from "@hooks";
import { useAnalytics } from "@hooks/common/analytics/use-analytics";
import { useLanguageSettings } from "@hooks/common/settings/use-language-settings";
import useExitBootcamp from "@hooks/game/bootcamp/use-exit-bootcamp";
import { useGlobalContext } from "@hooks/index";
import useI18n from "@i18n";
import { Chip, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { Api } from "@Shared/schemas";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const DEFAULT_HEIGHT = 92;

interface BootcampHeaderProps {
  height?: number;
  showContent?: boolean;
}

const BootcampHeader = ({ height = DEFAULT_HEIGHT, showContent = true }: BootcampHeaderProps) => {
  const theme = useTheme();
  const location = useLocation();
  const hideForRoute = location.state?.hideHeader === true;
  const { globalState, dispatch } = useGlobalContext();
  const { track } = useAnalytics();
  const { i18n } = useI18n();
  const { currentLocale, isLoading: isSavingLanguage, supportedLanguages, handleLocaleChange } = useLanguageSettings();
  const [showSkipConfirmation, setShowSkipConfirmation] = useState(false);
  const [headerAction, setHeaderAction] = useState<"skip" | "complete">("skip");
  const activeHeaderAction = location.state?.bootcampComplete === true ? "complete" : headerAction;
  const exitBootcamp = useExitBootcamp();
  const { call: skipBootcamp, loading: isSkipping } = useApi({
    api: Api.Users.SkipBootcamp,
    request: {},
    onSuccess: () => {
      setShowSkipConfirmation(false);
      dispatch({
        type: Actions.UsersActions.GetAccountInfo,
        payload: { disableLoaders: true },
      });
    },
  });

  useEffect(() => {
    dispatch({
      type: Actions.CoreActions.UpdateState,
      payload: { app: { navBarHeight: height } },
    });
    Events.app.setBootcampHeaderAction.uniq(setHeaderAction);

    return () => {
      Events.app.setBootcampHeaderAction.remove(setHeaderAction);
      dispatch({
        type: Actions.CoreActions.UpdateState,
        payload: { app: { navBarHeight: 0 } },
      });
    };
  }, [height]);

  const handleHeaderAction = () => {
    if (activeHeaderAction === "complete") {
      exitBootcamp();
      return;
    }

    if (!globalState.user.id || isSkipping) return;
    setShowSkipConfirmation(true);
  };

  const confirmSkip = async () => {
    if (!globalState.user.id || isSkipping) return;

    track(AnalyticsEvents.skip_bootcamp_clicked, { user_rating: globalState.user.userRating });

    try {
      await skipBootcamp();
    } catch {}
  };

  const keepTraining = () => {
    track(AnalyticsEvents.keep_training_clicked, { user_rating: globalState.user.userRating });
    setShowSkipConfirmation(false);
  };

  const logout = () => {
    dispatch({ type: Actions.FirebaseActions.Logout });
  };

  return (
    <HeaderBase sx={{ height, visibility: showContent && !hideForRoute ? "visible" : "hidden" }}>
      <HapticsButtonWrapper tooltip={i18n("header.menu.logout")}>
        <IconButton size="large" onClick={logout}>
          <Icon dsIcon={DSIcon.LOG_OUT} size={32} color={theme.palette.dsBase.v200} />
        </IconButton>
      </HapticsButtonWrapper>

      <Chip
        color="secondary"
        variant="outlined"
        label={i18n("label.league.bootcamp")}
        sx={{ position: "absolute", left: "50%", transform: "translateX(-50%)" }}
      />

      <Stack direction="row" spacing={2} alignItems="center">
        <BybDropdown
          value={currentLocale}
          ariaLabel={i18n("settings.language.title")}
          options={supportedLanguages}
          renderTrigger={triggerProps => (
            <BybButtonVR
              {...triggerProps}
              size="small"
              color="secondary"
              variant="outlined"
              shape="pill"
              startIcon={<Icon dsIcon={DSIcon.GLOBE} size={16} />}
              endIcon={<Icon dsIcon={DSIcon.CHEVRON_DOWN} size={16} />}
            >
              {currentLocale.toUpperCase()}
            </BybButtonVR>
          )}
          disabled={isSavingLanguage}
          onChange={handleLocaleChange}
        />
        <BybButtonVR size="small" color="secondary" variant="outlined" shape="pill" onClick={handleHeaderAction}>
          {i18n(activeHeaderAction === "complete" ? "session-results.bootcamp.continue-to-full-experience" : "skip")}
        </BybButtonVR>
      </Stack>

      <BybDialog
        name="skip-bootcamp-confirmation"
        open={showSkipConfirmation}
        onClose={() => setShowSkipConfirmation(false)}
        showCloseButton
        width="40vw"
      >
        <Stack spacing={5} textAlign="center" padding={3}>
          <Typography variant="text4xl700" color="dsBase.v200">
            {i18n("bootcamp.skip.confirmation.title")}
          </Typography>
          <Typography variant="textLg400" color="dsNeutral.v400" width="80%" alignSelf="center" textAlign="center">
            {i18n("bootcamp.skip.confirmation.description")}
          </Typography>

          <Stack direction="row" spacing={2}>
            <BybButtonVR color="secondary" size="large" fullWidth onClick={confirmSkip} loading={isSkipping} shape="pill">
              {i18n("bootcamp.skip.confirmation.confirm")}
            </BybButtonVR>
            <BybButtonVR size="large" fullWidth onClick={keepTraining} shape="pill">
              {i18n("bootcamp.skip.confirmation.cancel")}
            </BybButtonVR>
          </Stack>
        </Stack>
      </BybDialog>
    </HeaderBase>
  );
};

export default BootcampHeader;
